import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  Reservation,
  ReservationStats,
  ReservationSettings,
} from "../../api/reservationsAPI";

// Cache TTL: 2 minutes
const CACHE_TTL_MS = 2 * 60 * 1000;

interface CacheEntry {
  reservations: Reservation[];
  ts: number;
}

export type ViewMode = "day" | "week" | "month";

interface ReservationsState {
  // UI state — persists across navigation
  view: ViewMode;
  currentDate: string; // YYYY-MM-DD
  liveActive: boolean;

  // Data
  reservations: Reservation[];
  stats: ReservationStats | null;
  settings: ReservationSettings | null;
  settingsLoaded: boolean;

  // Loading flags
  loading: boolean;
  statsLoading: boolean;

  // Range cache: key = "from|to" → fresh data
  rangeCache: Record<string, CacheEntry>;
}

const today = new Date().toISOString().slice(0, 10);

const initialState: ReservationsState = {
  view: "day",
  currentDate: today,
  liveActive: false,
  reservations: [],
  stats: null,
  settings: null,
  settingsLoaded: false,
  loading: false,
  statsLoading: false,
  rangeCache: {},
};

export const reservationsSlice = createSlice({
  name: "reservations",
  initialState,
  reducers: {
    // ── UI ──────────────────────────────────────────────────────────────────
    setView(state, action: PayloadAction<ViewMode>) {
      state.view = action.payload;
    },
    setCurrentDate(state, action: PayloadAction<string>) {
      state.currentDate = action.payload;
    },
    setLiveActive(state, action: PayloadAction<boolean>) {
      state.liveActive = action.payload;
    },

    // ── Loading ──────────────────────────────────────────────────────────────
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setStatsLoading(state, action: PayloadAction<boolean>) {
      state.statsLoading = action.payload;
    },

    // ── Data ─────────────────────────────────────────────────────────────────
    setReservations(state, action: PayloadAction<Reservation[]>) {
      state.reservations = action.payload;
    },
    setStats(state, action: PayloadAction<ReservationStats | null>) {
      state.stats = action.payload;
    },
    setSettings(state, action: PayloadAction<ReservationSettings>) {
      state.settings = action.payload;
      state.settingsLoaded = true;
    },

    // ── Cache ─────────────────────────────────────────────────────────────────
    setCacheEntry(
      state,
      action: PayloadAction<{ key: string; reservations: Reservation[] }>
    ) {
      state.rangeCache[action.payload.key] = {
        reservations: action.payload.reservations,
        ts: Date.now(),
      };
      state.reservations = action.payload.reservations;
      state.loading = false;
    },
    invalidateCache(state) {
      state.rangeCache = {};
    },

    // ── Optimistic updates (no re-fetch needed) ────────────────────────────
    patchReservation(
      state,
      action: PayloadAction<{ id: string; changes: Partial<Reservation> }>
    ) {
      state.reservations = state.reservations.map((r) =>
        r._id === action.payload.id ? { ...r, ...action.payload.changes } : r
      );
      // Also patch inside cache entries so stale cache stays consistent
      for (const key of Object.keys(state.rangeCache)) {
        state.rangeCache[key].reservations = state.rangeCache[
          key
        ].reservations.map((r) =>
          r._id === action.payload.id ? { ...r, ...action.payload.changes } : r
        );
      }
    },
    removeReservation(state, action: PayloadAction<string>) {
      state.reservations = state.reservations.filter(
        (r) => r._id !== action.payload
      );
      for (const key of Object.keys(state.rangeCache)) {
        state.rangeCache[key].reservations = state.rangeCache[
          key
        ].reservations.filter((r) => r._id !== action.payload);
      }
    },
  },
});

export const {
  setView,
  setCurrentDate,
  setLiveActive,
  setLoading,
  setStatsLoading,
  setReservations,
  setStats,
  setSettings,
  setCacheEntry,
  invalidateCache,
  patchReservation,
  removeReservation,
} = reservationsSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────

/** Returns cached reservations for a range key if still fresh, else null */
export const selectCachedRange = (
  state: any,
  key: string
): Reservation[] | null => {
  const entry: CacheEntry | undefined = state.reservations.rangeCache[key];
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) return null;
  return entry.reservations;
};

export default reservationsSlice.reducer;
